import {Card, CardBody, Chip, Switch} from "@heroui/react";
import {createFileRoute} from '@tanstack/react-router';
import {useState} from "react";
import {UserRole} from "../../../constants.ts";
import Header from "../../../Header.tsx";
import {useJwtUser} from "../../../JWTAuthProvider.tsx";
import {InlineErrors} from "../../../scoring/layouts/shared.tsx";
import {profileOf} from "../../../scoring/previewRegistry.ts";
import samples from "../../../scoring/samples.json";
import GameScoreInput from "../../../tournaments/GameScoreInput.tsx";

export const Route = createFileRoute('/_app/master/scorecards')({
    component: RouteComponent,
});

/**
 * Master only: every scorecard the score modal can render (the nine layouts plus cricket), each
 * with a sample profile, in entry mode (live, with the payload it would send) and display mode
 * (each sample result read-only). Profiles come from the flag-ON registry fixture, so the page
 * reads the same whichever way the platform flag is set.
 */
type SampleResult = {title: string; sets: number[][]; score_meta: Record<string, unknown> | null; knockout?: boolean; league?: boolean; wickets?: number[]; scorecard_data?: unknown};
type SampleCard = {sport: string; scorecard: string; label: string; sides: [string, string]; results: SampleResult[]};

const REFUSAL = 'Preview: this is how the server\'s refusal reads under the scorecard.';

function RouteComponent() {
    const user = useJwtUser();
    const role = user?.role_override || user?.role;
    if (!user || role !== UserRole.Master) {
        return <><Header /><div className="py-12 text-center text-default-500">Master only.</div></>;
    }

    return <ScorecardsPreview />;
}

/** The page body, gate aside: what the render test proves draws every scorecard without throwing. */
export function ScorecardsPreview() {
    return (
        <>
            <Header />
            <div className="max-w-6xl mx-auto px-4 pb-12">
                <h1 className="text-2xl font-bold mt-4 mb-1">Scorecards</h1>
                <p className="text-sm text-default-500 mb-6">
                    Every scorecard the score modal renders, with a sample event each. Entry is live: type a score and the payload the modal would send is shown beside it.
                    Display shows each sample result as a visitor sees it. The universal footer sits under every entry card.
                </p>
                {(samples.scorecards as SampleCard[]).map((card) => <ScorecardSection key={card.sport} card={card} />)}
                <OutcomesSection />
            </div>
        </>
    );
}

function sampleGame(card: SampleCard, result: SampleResult | null, finished: boolean) {
    return {
        uid:        `preview-${card.sport}`,
        version:    1,
        sets:       result?.sets ?? [],
        sets_count: card.scorecard === 'sets' ? 5 : 1,
        score_meta: result?.score_meta ?? null,
        scorecard:  result?.scorecard_data ?? null,
        wickets:    result?.wickets ?? null,
        finished,
        t1_info:    {name: card.sides[0]},
        t2_info:    {name: card.sides[1]},
        league_uid: result?.league ? 'preview-league' : null,
    };
}

function ScorecardSection({card}: {card: SampleCard}) {
    const profile = profileOf(card.sport);
    const [payload, setPayload] = useState<unknown>(null);
    const [refuse, setRefuse] = useState(false);
    const entryGame = sampleGame(card, null, false);

    return (
        <section className="mb-10" data-testid={`preview-${card.scorecard}-${card.sport}`}>
            <div className="flex flex-wrap items-center gap-2 mb-3">
                <h2 className="text-lg font-bold">{card.label}</h2>
                <Chip size="sm" color="primary" variant="flat">{card.scorecard}</Chip>
                <Chip size="sm" variant="flat">{profile.sportKey}</Chip>
            </div>
            <div className="grid gap-4 lg:grid-cols-2">
                <Card shadow="sm" className="border border-default-200">
                    <CardBody className="p-4">
                        <div className="flex items-center justify-between mb-3">
                            <span className="text-xs font-bold uppercase tracking-wide text-default-500">Entry</span>
                            <Switch size="sm" isSelected={refuse} onValueChange={setRefuse}>Simulate a refusal</Switch>
                        </div>
                        <GameScoreInput
                            key={`${card.sport}-${refuse ? 'refuse' : 'accept'}`}
                            game={entryGame}
                            idx={0}
                            playersMap={{}}
                            profileOverride={profile}
                            knockout={card.results.some((r) => r.knockout)}
                            bonusPoints={card.sport === 'rugby'}
                            t1Name={card.sides[0]}
                            t2Name={card.sides[1]}
                            user={{uid: 'preview'}}
                            closeModal={() => {}}
                            updateGame={(_g: unknown, values: unknown) => {
                                setPayload(values);
                                return Promise.resolve(refuse ? {message: REFUSAL, errors: {sets: [REFUSAL]}} : {});
                            }}
                        />
                        <div className="mt-3">
                            <div className="text-xs font-bold uppercase tracking-wide text-default-500 mb-1">Payload sent</div>
                            <pre className="text-xs bg-default-100 rounded-lg p-2 overflow-x-auto" data-testid="preview-payload">{payload ? JSON.stringify(payload, null, 2) : 'Nothing sent yet.'}</pre>
                        </div>
                    </CardBody>
                </Card>
                <Card shadow="sm" className="border border-default-200">
                    <CardBody className="p-4 space-y-4">
                        <span className="text-xs font-bold uppercase tracking-wide text-default-500">Display</span>
                        {card.results.map((result) => (
                            <div key={result.title} className="rounded-xl border border-default-100 p-3">
                                <div className="text-sm font-semibold mb-2">{result.title}</div>
                                <GameScoreInput
                                    game={sampleGame(card, result, true)}
                                    idx={0}
                                    playersMap={{}}
                                    profileOverride={profile}
                                    knockout={!!result.knockout}
                                    bonusPoints={card.sport === 'rugby'}
                                    t1Name={card.sides[0]}
                                    t2Name={card.sides[1]}
                                    user={null}
                                    closeModal={() => {}}
                                    updateGame={() => Promise.resolve({})}
                                />
                                {result.score_meta && (
                                    <pre className="text-xs bg-default-100 rounded-lg p-2 mt-2 overflow-x-auto">score_meta {JSON.stringify(result.score_meta)}</pre>
                                )}
                            </div>
                        ))}
                    </CardBody>
                </Card>
            </div>
        </section>
    );
}

/** The footer's outcomes as the server stores them, and the inline refusal on its own. */
function OutcomesSection() {
    const outcomes = samples.outcomes as Array<{title: string; score_meta: Record<string, unknown>}>;
    return (
        <section className="mb-10" data-testid="preview-outcomes">
            <h2 className="text-lg font-bold mb-3">Footer outcomes and the inline refusal</h2>
            <div className="grid gap-4 lg:grid-cols-2">
                <Card shadow="sm" className="border border-default-200">
                    <CardBody className="p-4">
                        <span className="text-xs font-bold uppercase tracking-wide text-default-500">score_meta each outcome writes</span>
                        <ul className="mt-2 space-y-1 text-sm">
                            {outcomes.map((o) => (
                                <li key={o.title} className="flex flex-wrap items-baseline gap-2">
                                    <span className="font-semibold">{o.title}</span>
                                    <code className="text-xs bg-default-100 rounded px-1.5 py-0.5">{JSON.stringify(o.score_meta)}</code>
                                </li>
                            ))}
                        </ul>
                    </CardBody>
                </Card>
                <Card shadow="sm" className="border border-default-200">
                    <CardBody className="p-4">
                        <span className="text-xs font-bold uppercase tracking-wide text-default-500 block mb-2">The server's 422, in the sport's words</span>
                        <InlineErrors errors={['The winner must reach 3 legs.', 'A level score in a knockout needs a decider.']} />
                    </CardBody>
                </Card>
            </div>
        </section>
    );
}
